import { PDFDocument, StandardFonts, rgb, type PDFFont, type PDFImage } from 'pdf-lib'
import type { FormTemplate } from '../template/types'
import {
  layoutDocument,
  MARGIN_BOTTOM,
  PAGE_H,
  PAGE_W,
  type DrawOp,
  type Measurer,
  type PdfMeta,
  type SignatureImage,
  type Style,
} from './layout'
import { sanitise } from './text'

/**
 * Turns the laid-out draw commands into a PDF.
 *
 * Does no layout of its own: everything about where things go is decided in
 * `layout.ts`, so this file stays a dumb translation from top-origin
 * coordinates to the bottom-origin ones a PDF uses.
 */

const INK = rgb(0, 0, 0)
const SOFT = rgb(0.33, 0.33, 0.33)
const RULE_SOFT = rgb(0.67, 0.67, 0.67)

export interface SignaturePng {
  key: string
  bytes: Uint8Array
}

export interface RenderResult {
  bytes: Uint8Array
  pageCount: number
  /** True if any character had to be substituted to fit the PDF font. */
  substituted: boolean
}

export async function renderPdf(
  template: FormTemplate,
  values: Record<string, string>,
  signatures: SignaturePng[],
  meta: PdfMeta,
): Promise<RenderResult> {
  const doc = await PDFDocument.create()

  const fonts: Record<Style, PDFFont> = {
    regular: await doc.embedFont(StandardFonts.TimesRoman),
    bold: await doc.embedFont(StandardFonts.TimesRomanBold),
    italic: await doc.embedFont(StandardFonts.TimesRomanItalic),
    boldItalic: await doc.embedFont(StandardFonts.TimesRomanBoldItalic),
    sans: await doc.embedFont(StandardFonts.Helvetica),
  }

  const embedded = new Map<string, PDFImage>()
  const dimensions: Record<string, SignatureImage> = {}
  for (const signature of signatures) {
    const image = await doc.embedPng(signature.bytes)
    embedded.set(signature.key, image)
    dimensions[signature.key] = { width: image.width, height: image.height }
  }

  let substituted = false
  const clean = (text: string): string => {
    const result = sanitise(text)
    if (result.substituted) substituted = true
    return result.text
  }

  const measure = measurerFor(fonts)
  const cleanMeta: PdfMeta = { ...meta, signerName: clean(meta.signerName) }
  const cleanValues: Record<string, string> = {}
  for (const [key, value] of Object.entries(values)) cleanValues[key] = clean(value)

  const pages = layoutDocument(template, cleanValues, dimensions, cleanMeta, measure)

  for (const ops of pages) {
    const page = doc.addPage([PAGE_W, PAGE_H])
    for (const op of ops) draw(page, op, fonts, embedded, clean)
  }

  doc.setTitle(`${template.title} - ${cleanMeta.signerName}`)
  doc.setSubject(`${template.id} v${template.version}`)
  doc.setProducer(`Consent ${meta.appVersion}`)
  doc.setCreator(`Consent ${meta.appVersion}`)

  return { bytes: await doc.save(), pageCount: pages.length, substituted }
}

function draw(
  page: ReturnType<PDFDocument['addPage']>,
  op: DrawOp,
  fonts: Record<Style, PDFFont>,
  images: Map<string, PDFImage>,
  clean: (text: string) => string,
): void {
  switch (op.op) {
    case 'text': {
      const text = clean(op.text)
      if (!text) return
      page.drawText(text, {
        x: op.x,
        // Layout measures down from the page top; a PDF measures up from the
        // bottom.
        y: PAGE_H - op.baseline,
        size: op.size,
        font: fonts[op.style],
        color: op.color === 'ink' ? INK : SOFT,
      })
      break
    }
    case 'line':
      page.drawLine({
        start: { x: op.x1, y: PAGE_H - op.y },
        end: { x: op.x2, y: PAGE_H - op.y },
        thickness: op.thickness,
        color: op.color === 'ink' ? INK : RULE_SOFT,
      })
      break
    case 'image': {
      const image = images.get(op.key)
      if (!image) return
      page.drawImage(image, {
        x: op.x,
        y: PAGE_H - op.top - op.height,
        width: op.width,
        height: op.height,
      })
      break
    }
  }
}

/** Text metrics straight from the embedded standard fonts. */
export function measurerFor(fonts: Record<Style, PDFFont>): Measurer {
  return {
    width(text, style, size) {
      // Sanitised first: measuring text the font cannot encode throws, and a
      // width query must never be the thing that loses a signed form.
      return fonts[style].widthOfTextAtSize(sanitise(text).text, size)
    },
    ascent(style, size) {
      // Distance from the top of the line box down to the baseline, which is
      // the ascender alone.
      return fonts[style].heightAtSize(size, { descender: false })
    },
    lineHeight(style, size) {
      // Ascender plus descender is the tightest the type can set without
      // descenders touching the next line's ascenders, so the leading is a
      // fraction on top of that rather than on the ascender alone. Getting this
      // wrong is invisible to a page-count test and obvious on paper.
      return fonts[style].heightAtSize(size) * 1.18
    },
  }
}

/** Where the footer baseline sits, exported so tests can assert nothing overruns it. */
export const FOOTER_BASELINE = PAGE_H - MARGIN_BOTTOM + 22
